import { Autocomplete, Chip, TextField } from '@mui/material'

import { PRIMARY_MENU } from '../../model/website'

/**
 * The menus a route appears in, as a picker.
 *
 * **Free entry on purpose.** The set is open — the platform enumerates
 * no menus, because a site invents "footer" or "utility" and the
 * platform has no business owning that vocabulary — so this offers what
 * the site already uses and accepts anything else. `options` is
 * therefore a convenience against typos rather than a constraint: the
 * second route to join the footer picks it from the list, and a
 * misspelling shows up as a menu with one page in it.
 *
 * It replaced a "Show it in the menu" switch, which was the right
 * control while there was one menu and stopped being able to say
 * anything the moment there were two. A privacy policy in the footer and
 * not in the header is the ordinary case, and a boolean could not
 * express it.
 */
export function MenuTags({
  value,
  options,
  onChange,
  disabled,
  label = 'Menus',
  helperText = 'Which menus this page appears in. Leave it empty and the page is still reachable — it just is not listed anywhere.',
}: {
  value: string[]
  options: string[]
  onChange: (menus: string[]) => void
  disabled?: boolean
  label?: string
  helperText?: string
}) {
  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      options={options}
      value={value}
      // Trimmed and de-duplicated here rather than on the way out: the
      // model refuses a route naming one menu twice, and an author who
      // typed a trailing space should not meet that as a 400.
      onChange={(_, next) => {
        const cleaned: string[] = []
        for (const entry of next as string[]) {
          const menu = entry.trim()
          if (menu && !cleaned.includes(menu)) cleaned.push(menu)
        }
        onChange(cleaned)
      }}
      renderTags={(menus, getTagProps) =>
        menus.map((menu, index) => {
          const { key, ...props } = getTagProps({ index })
          return (
            <Chip
              key={key}
              {...props}
              size="small"
              label={menu}
              color={menu === PRIMARY_MENU ? 'primary' : 'default'}
              variant={menu === PRIMARY_MENU ? 'filled' : 'outlined'}
            />
          )
        })
      }
      renderInput={(params) => <TextField {...params} label={label} helperText={helperText} />}
    />
  )
}
